import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import parse from 'html-react-parser';
import moment from 'moment';
import { Button } from '@/components/customercenter-ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/customercenter-ui/components/ui/dialog';
import { Input } from '@/components/customercenter-ui/components/ui/input';
import { ScrollArea } from '@/components/customercenter-ui/components/ui/scroll-area';
import BranchSearch from '@/components/customercenter-ui/svgs/brandSearch';
import { useCustomObjects } from '@/frontastic';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';

type ObjectType = {
  [key: string]: string[];
};

const normalizeString = (str: string) => {
  return str.toLowerCase().replace(/[^a-z0-9]/g, '');
};

const ExcludedBrandsDialog = ({ excludedBrandsContent }: any) => {
  const { getExcludedBrandsData } = useCustomObjects();
  const [brands, setBrands] = useState<ObjectType>({});
  const [filteredBrands, setFilteredBrands] = useState<ObjectType>({});
  const [query, setQuery] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const fetchExcludedBrandsDataStorage = () => {
    let excludedBrandsDataFromStorage;
    const excludedBrandsDataLocalStorage = localStorage.getItem('excludedBrandsData');
    if (excludedBrandsDataLocalStorage) {
      try {
        const excludedBrandsData = JSON.parse(excludedBrandsDataLocalStorage);
        if (excludedBrandsData) {
          const { data, isSucess, date } = excludedBrandsData;
          if (isSucess && date && data) {
            const dateObj = moment(date);
            const currentDate = new Date();
            const currentDateObj = moment(currentDate?.getTime());
            const isSameDate = dateObj.isSame(currentDateObj, 'day');
            const dateObjHour = dateObj.hours();
            const currentDateHour = currentDateObj.hours();
            if (isSameDate && dateObjHour === currentDateHour) {
              excludedBrandsDataFromStorage = data;
            }
          }
        }
      } catch (error) {
        console.error('Error while parsing deliveryDateData');
      }
    }
    return excludedBrandsDataFromStorage;
  };

  const fetchExcludedBrandsData = async () => {
    try {
      const excludedBrandsDataFromStorage = fetchExcludedBrandsDataStorage();
      if (excludedBrandsDataFromStorage) {
        setBrands(excludedBrandsDataFromStorage);
        setFilteredBrands(excludedBrandsDataFromStorage);
      } else {
        const excludedBrands = await getExcludedBrandsData();
        if (excludedBrands && Object.entries(excludedBrands)?.length > 0) {
          const excludedBrandsData = {
            data: excludedBrands,
            isSucess: true,
            date: new Date(),
          };
          localStorage.setItem('excludedBrandsData', JSON.stringify(excludedBrandsData));
          setBrands(excludedBrands);
          setFilteredBrands(excludedBrands);
        }
      }
    } catch (error) {
      console.error('Error while fetching excluded brand data.');
    }
  };

  useEffect(() => {
    fetchExcludedBrandsData();
  }, []);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const lowerCaseQuery = normalizeString(event.target.value);
    setQuery(event.target.value);
    const newFilteredBrands: ObjectType = {};

    Object.entries(brands).forEach(([key, values]) => {
      const matchingValues = values.filter((brand) => normalizeString(brand).includes(lowerCaseQuery));
      if (matchingValues.length > 0) {
        newFilteredBrands[key] = matchingValues;
      }
    });

    setFilteredBrands(newFilteredBrands);
  };

  const handleDialogClose = () => {
    setIsOpen(false);
    setQuery('');
    setFilteredBrands(brands);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="m-0 border-0 p-0 text-[#0075C9] underline" onClick={handleDialogClose}>
          Excluded Brands
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white sm:max-w-[800px]">
        <DialogTitle className="text-left text-2xl font-bold">
          {mapLocaleToMeaningfulFormat().excludedBrand}
        </DialogTitle>
        <DialogDescription>
          <p className="pb-7 text-left lg:pb-4">{mapLocaleToMeaningfulFormat().brandDesc}</p>
          <div className="relative mb-5 max-w-[268px]">
            <Input
              id="name"
              placeholder="Search brands"
              className="gbunmask col-span-3 border-[#B6B6B8] text-sm text-[#656668]"
              onChange={handleSearch}
              value={query}
            />
            <Button
              className="absolute right-0 top-0 rounded-none rounded-r-md bg-[#002C4E] fill-white pl-3 text-3xl text-white"
              type="button"
              aria-label="Search"
            >
              <BranchSearch />
            </Button>
          </div>
          <ScrollArea className="mb-5 h-72">
            <section className="max-h-[1500px] flex-col flex-wrap gap-x-4 px-5 lg:flex">
              {Object.entries(filteredBrands)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, values]) => (
                  <section key={key} className="mb-6">
                    <h4 className="text-sm font-bold text-black">{key}</h4>
                    {values.map((brandName) => (
                      <ul key={brandName}>
                        <li className="text-left text-sm leading-5 text-black">{brandName}</li>
                      </ul>
                    ))}
                  </section>
                ))}
            </section>
          </ScrollArea>
          <section className="border border-dashed border-[#B6B6B8] p-4">
            <p className="mb-4">
              Promotion valid on applicable items only. Only one offer or discount per site may be used at a time. No
              credits or adjustments will be made on previous purchases. This offer is not redeemable for cash. Enter
              the promo code in the Promo Code box, then click on &quot;apply code&quot; to receive the discount.
              Promotion not valid after expiration date.
            </p>
            <p className="mb-4">
              **Certain brand items are excluded from promotions. Add item to cart and apply promo code to see if promo
              code is valid on the item
            </p>
            <p>
              <strong>Contact Us For Additional Assistance</strong>
            </p>
            <p>
              We are available to assist you from 6 a.m. – Midnight CST, 7 days a week. Phone: 1-800-704-5480 or{' '}
              <Link href={''} className="underline">
                Contact Us
              </Link>
              .
            </p>
          </section>
          <section>{excludedBrandsContent ? parse(excludedBrandsContent) : <></>}</section>
        </DialogDescription>
      </DialogContent>
    </Dialog>
  );
};

export default ExcludedBrandsDialog;
